/**
 * #1152 / #1217 — the one-time code that proves an account can receive email at an address: the add-email code
 * (Settings → Emails) and the invitation code (accepting from an account that hasn't proven the invited address). ONE
 * spelling of how a typed code is read, shared by the server (`@papan/auth` `proof-code.ts`) and both pages, so a
 * button and the check can't disagree about what counts as a code.
 *
 * 6 digits, as email codes almost everywhere are (#1217: a 10-character code is not something people expect to type).
 * The length isn't what protects it — the guess budget is: 5 wrong guesses per (account, address) per window, never
 * restored by a new code (`checkProofCode`).
 */
export const PROOF_CODE_LENGTH = 6

/** What a person types: spaces and a dash don't matter, and full-width digits (a CJK IME's １２３) are digits. */
export function normalizeProofCode(code: string): string {
  return String(code).normalize('NFKC').replace(/[\s\-\u2010-\u2015\u2212]/g, '')
}

/** The ONE answer to "what did the person send?" — both routes and both pages ask this:
 *  - `empty`: nothing that counts (no code at all);
 *  - `incomplete`: a valid start, still being typed (a page shows nothing; the server treats it as malformed);
 *  - `malformed`: can never match — anything but a digit, or too long. Refused without spending a guess; a page shows
 *    a hint;
 *  - `ok`: the normalised code to compare. */
export function classifyProofCode(raw: unknown):
  { kind: 'empty' } | { kind: 'incomplete' } | { kind: 'malformed' } | { kind: 'ok'; code: string } {
  const code = typeof raw === 'string' ? normalizeProofCode(raw) : ''
  if (!code) return { kind: 'empty' }
  if (code.length > PROOF_CODE_LENGTH || !/^[0-9]+$/.test(code)) return { kind: 'malformed' }
  return code.length < PROOF_CODE_LENGTH ? { kind: 'incomplete' } : { kind: 'ok', code }
}

/** The proof-code refusals a page shows in its own words, by the error's `code` — both flows (add-email, invitation). */
const PROOF_REFUSALS: Readonly<Record<string, ProofRefusalKind>> = {
  EMAIL_CODE_TOO_MANY: 'guessesSpent', INVITE_CODE_TOO_MANY: 'guessesSpent',
  EMAIL_CODE_SEND_LIMIT: 'sendLimit', INVITE_CODE_SEND_LIMIT: 'sendLimit',
  EMAIL_CODE_MALFORMED: 'malformed', INVITE_CODE_MALFORMED: 'malformed',
}
export type ProofRefusalKind = 'guessesSpent' | 'sendLimit' | 'malformed'

/**
 * Which proof-code refusal a failed request is, or `null` for any other failure (#1217). A page renders the
 * `common:proofCode.<kind>` string — the person's language — instead of the server's English sentence, which stays for
 * API callers. One table for both pages, so they can't disagree about which refusal is which.
 */
export function proofRefusalKind(err: unknown): ProofRefusalKind | null {
  const c = (err as { code?: unknown } | null | undefined)?.code
  return typeof c === 'string' && Object.prototype.hasOwnProperty.call(PROOF_REFUSALS, c) ? PROOF_REFUSALS[c] : null
}

/**
 * After a "send a new code" request FAILED, is the code the person already typed still the live one? The ONE rule both
 * pages use to decide whether to put it back (#1217). Only a send-limit refusal (`*_CODE_SEND_LIMIT`) says so for sure:
 * the issue step refuses before it touches anything. Every other failure may mean the code changed or died (a 5xx or
 * timeout after a new code was stored, a spent budget that burnt it, an address verified meanwhile), so the box stays
 * cleared — retyping a live code costs nothing; Verify on a dead one costs a guess.
 */
export function typedCodeSurvivesFailedSend(err: unknown): boolean {
  const c = (err as { code?: unknown } | null | undefined)?.code
  return typeof c === 'string' && c.endsWith('_CODE_SEND_LIMIT')
}
